import {useEffect, useMemo, useRef} from "react";
import {SpaceEditorSettings} from "../spacecontrolbar/SpaceEditorSettings";
import type {SpaceViewRenderer} from "../../logic3d/renderers/SpaceViewRenderer";
import {ReactUtils} from "../../../../../utils/ReactUtils";
import {PointerDetector} from "../../../../../../utils/interaction/PointerDetector";
import type {ISignal} from "../../../../../../utils/signal/ISignal";
import {getFloatingOverlayStyle, useFloatingOverlayPosition} from "../FloatingOverlayUtils";

interface ISpaceContextMenuProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly worldX?: number;
	readonly worldY?: number;
	readonly getWorldPosition?: () => {x: number; y: number};
	readonly positionUpdateSignal?: ISignal;
	readonly onClose: () => void;
	readonly onCopyClick: () => void;
	readonly onCopyToStampClick: () => void;
	readonly onCutClick: () => void;
	readonly onDeleteClick: () => void;
	readonly onPasteClick: () => void;
	readonly onRealignSpaceClick: () => void;
}

export const SpaceContextMenu = (props: ISpaceContextMenuProps) => {
	const _ref = useRef<HTMLDivElement>(null);
	const {spaceViewRenderer} = props;
	const selectedItems = spaceViewRenderer.spaceItemController.selectedItems;
	const isCopyEnabled = selectedItems.length > 0 && !spaceViewRenderer.ghostModeManager.isActive;
	const selectableClasses = "selectable hbox";
	const hotkeyClasses = "marginLeftAuto display_flex alignCenter";
	const getWorldPosition = props.getWorldPosition ?? (() => ({x: props.worldX ?? 0, y: props.worldY ?? 0}));
	const positionSignals = useMemo(() => (props.positionUpdateSignal ? [props.positionUpdateSignal] : []), [props.positionUpdateSignal]);

	useFloatingOverlayPosition({
		elementRef: _ref,
		spaceViewRenderer,
		getWorldPosition,
		position: "bottom",
		signals: positionSignals,
		watchCamera: false,
	});

	useEffect(() => {
		const {signals} = spaceViewRenderer.toolManager.cameraControls;

		signals.cameraGrabbed.add(props.onClose);
		signals.cameraPropsChange.add(props.onClose);

		return () => {
			signals.cameraGrabbed.remove(props.onClose);
			signals.cameraPropsChange.remove(props.onClose);
		};
	}, [props.onClose, spaceViewRenderer]);

	const onCopyToClipboardClick = () => {
		props.onClose();
		props.onCopyClick();
	};

	const onCopyToStampClick = () => {
		props.onClose();
		props.onCopyToStampClick();
	};

	const onCutClick = () => {
		props.onClose();
		props.onCutClick();
	};

	const onPasteClick = () => {
		props.onClose();
		props.onPasteClick();
	};

	const onDeleteClick = () => {
		props.onClose();
		props.onDeleteClick();
	};

	return (
		<div
			ref={_ref}
			className="SpaceContextMenu floatingDropdown vbox"
			style={getFloatingOverlayStyle({
				spaceViewRenderer,
				getWorldPosition,
				position: "bottom",
				element: _ref.current,
			})}
			onContextMenu={PointerDetector.onContextMenu}
		>
			<div
				className={ReactUtils.cls(selectableClasses, {disabled: !isCopyEnabled})}
				onClick={onCopyToClipboardClick}
			>
				<span>Copy</span>
				<span className={hotkeyClasses}>Ctrl + C</span>
			</div>
			<div
				className={ReactUtils.cls(selectableClasses, {disabled: !isCopyEnabled})}
				onClick={onCutClick}
			>
				<span>Cut</span>
				<span className={hotkeyClasses}>Ctrl + X</span>
			</div>
			<div
				className={ReactUtils.cls(selectableClasses, {disabled: !isCopyEnabled})}
				onClick={onCopyToStampClick}
			>
				<span>Copy to Stamp</span>
				<span className={hotkeyClasses}>Ctrl + S</span>
			</div>
			<div
				className={ReactUtils.cls(selectableClasses, {disabled: isCopyEnabled})}
				onClick={onPasteClick}
			>
				<span>Paste</span>
				<span className={hotkeyClasses}>Ctrl + V</span>
			</div>
			<div
				className={ReactUtils.cls(selectableClasses, {disabled: !isCopyEnabled})}
				onClick={onDeleteClick}
			>
				<span>Delete</span>
				<span className={hotkeyClasses}>Del</span>
			</div>
			<div className={`${selectableClasses} spaceEditorSettings`}>
				<span>Settings</span>
				<span className={hotkeyClasses}>▶</span>
				<SpaceEditorSettings
					spaceViewRenderer={spaceViewRenderer}
					onRealignSpaceClick={props.onRealignSpaceClick}
					onClose={props.onClose}
					closeOnBlur={false}
				/>
			</div>
		</div>
	);
};
